// Чистые правила валидации без зависимостей (тестируются в node --test).
// Контракт vee-validate: правило возвращает true или строку с ошибкой на русском.
// Все правила, кроме required, пропускают пустое значение — обязательность
// управляется отдельным правилом required.

const isEmpty = (value) =>
  value == null || (typeof value === 'string' && value.trim() === '');

export function required(value) {
  if (isEmpty(value)) return 'Поле обязательно для заполнения';
  return true;
}

export function fio(value) {
  if (isEmpty(value)) return true;
  const words = String(value).trim().split(/\s+/);
  if (words.length < 2) return 'Укажите фамилию и имя (минимум 2 слова)';
  if (!words.every((w) => /^[-А-Яа-яЁёA-Za-z]+$/.test(w))) {
    return 'ФИО может содержать только буквы';
  }
  return true;
}

const CIS_PHONE_PATTERNS = [
  /^7(?:9\d{9}|[67]\d{9})$/, // Россия и Казахстан
  /^375\d{9}$/, // Беларусь
  /^374\d{8}$/, // Армения
  /^994\d{9}$/, // Азербайджан
  /^996\d{9}$/, // Кыргызстан
  /^992\d{9}$/, // Таджикистан
  /^993\d{8}$/, // Туркменистан
  /^998\d{9}$/, // Узбекистан
];

export function normalizeCisPhone(value) {
  if (isEmpty(value)) return '';
  let digits = String(value).replace(/\D/g, '');
  if (/^9\d{9}$/.test(digits)) digits = `7${digits}`;
  if (/^8\d{10}$/.test(digits)) digits = `7${digits.slice(1)}`;
  return CIS_PHONE_PATTERNS.some((pattern) => pattern.test(digits))
    ? `+${digits}`
    : null;
}

export function phone(value) {
  if (isEmpty(value)) return true;
  return normalizeCisPhone(value) != null
    || 'Телефон страны СНГ укажите в международном формате';
}

export function inn(value) {
  if (isEmpty(value)) return true;
  const v = String(value).trim();
  if (!/^\d{10}$|^\d{12}$/.test(v)) return 'ИНН: 10 или 12 цифр';

  if (v.length === 10) {
    const weights = [2, 4, 10, 3, 5, 9, 4, 6, 8];
    const sum = weights.reduce((acc, w, i) => acc + w * Number(v[i]), 0);
    return (sum % 11) % 10 === Number(v[9]) || 'ИНН указан неверно';
  }

  const w1 = [7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  const w2 = [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8];
  const s1 = w1.reduce((acc, w, i) => acc + w * Number(v[i]), 0);
  const s2 = w2.reduce((acc, w, i) => acc + w * Number(v[i]), 0);
  return ((s1 % 11) % 10 === Number(v[10]) && (s2 % 11) % 10 === Number(v[11]))
    || 'ИНН указан неверно';
}

export function passport(value) {
  if (isEmpty(value)) return true;
  return /^\d{4}\s?\d{6}$/.test(String(value).trim())
    || 'Паспорт: 4 цифры серии и 6 цифр номера';
}

export function email(value) {
  if (isEmpty(value)) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim())
    || 'Некорректный email';
}

// Дата в формате ДД.ММ.ГГГГ; валидная, не ранее 01.01.1900.
export function date(value) {
  if (isEmpty(value)) return true;
  const parsed = parseDate(value);
  if (!parsed) return 'Дата в формате ДД.ММ.ГГГГ';
  if (parsed.getFullYear() < 1900) return 'Дата не может быть раньше 01.01.1900';
  return true;
}

// Дата + не в будущем (дата выдачи паспорта и т.п.).
export function datePast(value) {
  const base = date(value);
  if (base !== true) return base;
  if (isEmpty(value)) return true;
  const parsed = parseDate(value);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  if (parsed > today) return 'Дата не может быть в будущем';
  return true;
}

export function vin(value) {
  if (isEmpty(value)) return true;
  const v = String(value).trim().toUpperCase();
  return /^[A-HJ-NPR-Z0-9]{17}$/.test(v) || 'VIN: 17 символов, без букв I, O, Q';
}

export function ogrn(value) {
  if (isEmpty(value)) return true;
  const v = String(value).trim();
  if (!/^\d{13}$|^\d{15}$/.test(v)) return 'ОГРН: 13 или 15 цифр';

  if (v.length === 13) {
    const check = (Number(v.slice(0, 12)) % 11) % 10;
    return check === Number(v[12]) || 'ОГРН указан неверно';
  }
  const check = (Number(v.slice(0, 14)) % 13) % 10;
  return check === Number(v[14]) || 'ОГРНИП указан неверно';
}

export function digits(value) {
  if (isEmpty(value)) return true;
  return /^\d+$/.test(String(value).trim()) || 'Только цифры';
}

export function imei(value) {
  if (isEmpty(value)) return true;
  return /^\d{15}$/.test(String(value).trim()) || 'IMEI должен содержать 15 цифр';
}

function parseDate(value) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(String(value).trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  const d = new Date(year, month - 1, day);
  if (d.getDate() !== day || d.getMonth() !== month - 1 || d.getFullYear() !== year) {
    return null;
  }
  return d;
}
